import axios from 'axios';
import { attachRetry } from './httpRetry';
import { attachAuth, SESSION_EXPIRED_EVENT, withTokenParam } from './auth';
import { Task, Message, AITeamMember, HumanTeamMember, TaskCreate, MessageCreate, TaskStatus, Feature, FeatureCategory, FeaturesByCategory, Idea, IdeaCreate, IdeasByCategory, IdeaType, IdeaStatus, FeaturePriority, FeatureStatus, JsonlUpdate, ActiveSession, JsonlMessage, Release, ReleaseFixMode } from '../types';

// Use REACT_APP_API_URL from .env if available, otherwise use relative /api
// Nginx proxies /api/ to the backend, so we always use relative URL
const getApiBaseUrl = () => {
  // First check if REACT_APP_API_URL is set in .env
  if (process.env.REACT_APP_API_URL) {
    console.log('Using API URL from .env:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }

  // Always use relative URL - nginx proxies /api/ to backend
  // This works for both localhost (React dev server proxy) and production (nginx proxy)
  console.log('Using relative API URL /api');
  return '/api';
};

export const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Every call carries the session token: the API is closed to callers who aren't signed
// in (backend app/api/auth_gate.py). See services/auth.ts for how the token is obtained.
attachAuth(api);

// Re-exported so existing importers (App.tsx) keep working; it is defined in services/auth.ts
// alongside the interceptors that fire it.
export { SESSION_EXPIRED_EVENT };

// Retry idempotent GETs on transient network/DNS failures (DuckDNS resolution is flaky).
attachRetry(api);

// Team Members
export const getTeamMembers = async (): Promise<AITeamMember[]> => {
  const response = await api.get('/team');
  return response.data;
};

// Human Team Members
export const getHumanTeamMembers = async (): Promise<HumanTeamMember[]> => {
  const response = await api.get('/human-team');
  return response.data;
};

// Tasks
export const createTask = async (task: TaskCreate): Promise<Task> => {
  const response = await api.post('/tasks', task);
  return response.data;
};

export interface TaskListOptions {
  search?: string;
  // Defaults to the last 30 days server-side; pass true to load full history.
  allTime?: boolean;
  days?: number;
}

export const getTasks = async (
  aiTeamMember?: string,
  status?: TaskStatus,
  featureId?: string,
  createdBy?: string,
  isQuicktask?: boolean,
  options?: TaskListOptions
): Promise<Task[]> => {
  const params: any = {};
  if (aiTeamMember) params.ai_team_member = aiTeamMember;
  if (status) params.status = status;
  if (featureId) params.feature_id = featureId;
  if (createdBy) params.created_by = createdBy;
  if (isQuicktask !== undefined) params.is_quicktask = isQuicktask;
  if (options?.search) params.search = options.search;
  if (options?.allTime) params.all_time = true;
  if (options?.days !== undefined) params.days = options.days;

  const response = await api.get('/tasks', { params });
  return response.data;
};

export const getTasksByFeature = async (featureId: string): Promise<Task[]> => {
  const response = await api.get('/tasks', { params: { feature_id: featureId } });
  return response.data;
};

export const updateTaskFeature = async (taskId: number, featureId: string | null): Promise<Task> => {
  const response = await api.patch(`/tasks/${taskId}`, { feature_id: featureId });
  return response.data;
};

export const getTask = async (taskId: number): Promise<Task> => {
  const response = await api.get(`/tasks/${taskId}`);
  return response.data;
};

export const startTask = async (taskId: number): Promise<void> => {
  // Backend returns immediately and runs Claude in background
  await api.post(`/tasks/${taskId}/start`);
};

export const completeTask = async (taskId: number): Promise<void> => {
  await api.post(`/tasks/${taskId}/complete`);
};

export const stopTask = async (taskId: number): Promise<{ status: string; task_id: number }> => {
  const response = await api.post(`/tasks/${taskId}/stop`);
  return response.data;
};

export const deleteTask = async (taskId: number): Promise<void> => {
  await api.delete(`/tasks/${taskId}`);
};

// Soft-dismiss (archive) a task: it leaves the active lane but isn't destroyed.
// The default "get rid of it" action in the cockpit.
export const dismissTask = async (taskId: number): Promise<void> => {
  await api.post(`/tasks/${taskId}/dismiss`);
};

// Outcome of routing a task/ask to the current person's AI rep.
export interface HandleByAiResult {
  outcome: 'handled' | 'drafted';
  status: string;
  agent?: string;
  domain?: string;
  reason?: string;
  draft?: string | null;
  message?: string;
}

// "Let my AI handle it": route the task to the assigned person's AI rep. Either
// the rep takes it on (handled -> leaves the lane) or drafts a proposed answer
// for review (drafted -> the draft is returned + saved on the task).
export const handleTaskByAi = async (taskId: number): Promise<HandleByAiResult> => {
  const response = await api.post(`/tasks/${taskId}/handle-by-ai`);
  return response.data;
};

// Messages
export const createMessage = async (message: MessageCreate): Promise<Message> => {
  const response = await api.post('/messages', message);
  return response.data;
};

export const getMessages = async (taskId: number): Promise<Message[]> => {
  const response = await api.get(`/messages/${taskId}`);
  return response.data;
};

// WebSocket
export const connectWebSocket = (
  taskId: number,
  onMessage: (data: any) => void,
  onConnectionChange?: (connected: boolean) => void
): WebSocket => {
  // Determine WebSocket URL - use same host as page (nginx proxies /api/ to backend)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host; // includes port if non-standard
  const ws = new WebSocket(withTokenParam(`${protocol}//${host}/api/ws/${taskId}`));

  let pingInterval: NodeJS.Timeout | null = null;
  let hasConnected = false;

  ws.onopen = () => {
    hasConnected = true;
    console.log(`WebSocket connected for task ${taskId}`);
    if (onConnectionChange) onConnectionChange(true);

    // Start ping interval only after connection is established
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      } else {
        // Clear interval if connection is no longer open
        if (pingInterval) {
          clearInterval(pingInterval);
          pingInterval = null;
        }
      }
    }, 30000);
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  ws.onerror = (error) => {
    // Only log errors if the WebSocket was successfully connected before
    // Suppress errors from premature closure (React StrictMode)
    if (hasConnected) {
      console.error('WebSocket error for task', taskId, ':', error);
    }
  };

  ws.onclose = (event) => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }

    if (onConnectionChange) onConnectionChange(false);

    // Only log meaningful close events
    if (hasConnected) {
      if (event.wasClean) {
        console.log(`WebSocket closed cleanly for task ${taskId}`);
      } else {
        console.warn(`WebSocket connection closed unexpectedly for task ${taskId}`);
      }
    }
    // Suppress logs for connections that never fully established (React StrictMode cleanup)
  };

  return ws;
};

// Subscribe to the whole Tasks module: fires onChange whenever any task is created or
// changes status anywhere (including a task an autonomous agent created via manage_task.py),
// so a lane/dashboard can refetch and show it live. Mirrors connectWebSocket's URL + ping
// pattern. Auto-reconnects with a short backoff so a backend restart doesn't go stale.
export const connectTasksWebSocket = (
  onChange: (data: any) => void
): { close: () => void } => {
  let ws: WebSocket | null = null;
  let pingInterval: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  const open = () => {
    if (closed) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host; // includes port if non-standard
    ws = new WebSocket(withTokenParam(`${protocol}//${host}/api/ws/tasks`));

    ws.onopen = () => {
      pingInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && data.type === 'task_list_changed') onChange(data);
      } catch {
        // ignore non-JSON keepalive frames (e.g. "pong")
      }
    };

    ws.onclose = () => {
      if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
      if (!closed) {
        // Backend restarted or connection dropped — retry so the lane stays live.
        reconnectTimer = setTimeout(open, 3000);
      }
    };

    ws.onerror = () => {
      // Let onclose handle the reconnect; avoid noisy logging on StrictMode cleanup.
    };
  };

  open();

  return {
    close: () => {
      closed = true;
      if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    },
  };
};

// Name Autocomplete
export const getNameSuggestions = async (prefix: string = ""): Promise<string[]> => {
  const response = await api.get('/name-suggestions', { params: { prefix } });
  return response.data.suggestions;
};

export const getAllNames = async (): Promise<string[]> => {
  const response = await api.get('/all-names');
  return response.data.names;
};

// Analytics
export interface InteractionMatrix {
  humans: string[];
  ai_members: Array<{ name: string; role: string }>;
  matrix: { [human: string]: { [aiMember: string]: number } };
  totals: {
    by_human: { [human: string]: number };
    by_ai: { [aiMember: string]: number };
    overall: number;
  };
}

export interface StatusBreakdown {
  [status: string]: number;
}

export interface CompletionStats {
  total_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
  completion_rate: number;
  failure_rate: number;
  avg_completion_seconds: number;
  avg_completion_minutes: number;
}

export const getInteractionMatrix = async (): Promise<InteractionMatrix> => {
  const response = await api.get('/analytics/interaction-matrix');
  return response.data;
};

export const getStatusBreakdown = async (): Promise<StatusBreakdown> => {
  const response = await api.get('/analytics/status-breakdown');
  return response.data;
};

export const getCompletionStats = async (): Promise<CompletionStats> => {
  const response = await api.get('/analytics/completion-stats');
  return response.data;
};

export interface ContentAnalytics {
  totals: {
    features: number;
    ideas: number;
    tasks: number;
    prompts: number;
    completed_tasks: number;
  };
  by_category: {
    [categoryId: string]: {
      category_name: string;
      features: number;
      ideas: number;
      tasks: number;
      prompts: number;
      completed_tasks: number;
    };
  };
  by_human: {
    [humanName: string]: {
      ideas: number;
      tasks: number;
      prompts: number;
      completed_tasks: number;
    };
  };
}

export const getContentAnalytics = async (): Promise<ContentAnalytics> => {
  const response = await api.get('/analytics/content');
  return response.data;
};

// ============ FEATURE MANAGEMENT API ============

// Categories
export const getCategories = async (): Promise<FeatureCategory[]> => {
  const response = await api.get('/features/categories');
  return response.data;
};

export const createCategory = async (id: string, name: string, order?: number): Promise<FeatureCategory> => {
  const response = await api.post('/features/categories', { id, name, order });
  return response.data;
};

export const updateCategory = async (categoryId: string, name?: string, order?: number): Promise<FeatureCategory> => {
  const response = await api.put(`/features/categories/${categoryId}`, { name, order });
  return response.data;
};

export const deleteCategory = async (categoryId: string): Promise<void> => {
  await api.delete(`/features/categories/${categoryId}`);
};

export const reorderCategories = async (categoryIds: string[]): Promise<FeatureCategory[]> => {
  const response = await api.post('/features/categories/reorder', { category_ids: categoryIds });
  return response.data;
};

// Features
export const getFeatures = async (): Promise<Feature[]> => {
  const response = await api.get('/features');
  return response.data;
};

export const getFeaturesByCategory = async (): Promise<FeaturesByCategory> => {
  const response = await api.get('/features/by-category');
  return response.data;
};

export const getFeature = async (featureId: string): Promise<Feature> => {
  const response = await api.get(`/features/${featureId}`);
  return response.data;
};

export const createFeature = async (
  name: string,
  category: string,
  expectations?: string,
  order?: number,
  owner?: string,
  priority?: string
): Promise<Feature> => {
  const response = await api.post('/features', { name, category, expectations, order, owner, priority });
  return response.data;
};

export const updateFeature = async (
  featureId: string,
  updates: Partial<Feature>
): Promise<Feature> => {
  const response = await api.put(`/features/${featureId}`, updates);
  return response.data;
};

export const deleteFeature = async (featureId: string): Promise<void> => {
  await api.delete(`/features/${featureId}`);
};

export const moveFeature = async (
  featureId: string,
  newCategory: string,
  newOrder?: number
): Promise<Feature> => {
  const response = await api.post(`/features/${featureId}/move`, {
    category: newCategory,
    order: newOrder
  });
  return response.data;
};

export const reorderFeatures = async (
  categoryId: string,
  featureIds: string[]
): Promise<Feature[]> => {
  const response = await api.post('/features/reorder', {
    category_id: categoryId,
    feature_ids: featureIds
  });
  return response.data;
};

export const appendFeatureChange = async (
  featureId: string,
  entry: string
): Promise<void> => {
  await api.post(`/features/${featureId}/append-change`, { entry });
};

export const appendFeatureInput = async (
  featureId: string,
  entry: string,
  humanName: string
): Promise<void> => {
  await api.post(`/features/${featureId}/append-input`, { entry, human_name: humanName });
};

// General Prompt
export const getGeneralPrompt = async (): Promise<string> => {
  const response = await api.get('/features/general-prompt');
  return response.data.prompt;
};

export const updateGeneralPrompt = async (prompt: string): Promise<void> => {
  await api.put('/features/general-prompt', { prompt });
};

// AI Context
export const getFeatureAIContext = async (featureId: string): Promise<{ feature: Feature; context: string }> => {
  const response = await api.get(`/features/${featureId}/ai-context`);
  return response.data;
};

// Priority and Status
export const getPriorityLevels = async (): Promise<string[]> => {
  const response = await api.get('/features/priority-levels');
  return response.data.priority_levels;
};

export const getStatusValues = async (): Promise<string[]> => {
  const response = await api.get('/features/status-values');
  return response.data.status_values;
};

export const markFeatureDone = async (featureId: string): Promise<Feature> => {
  const response = await api.post(`/features/${featureId}/mark-done`);
  return response.data;
};

export const reopenFeature = async (featureId: string): Promise<Feature> => {
  const response = await api.post(`/features/${featureId}/reopen`);
  return response.data;
};

export const getFeaturesWithStatus = async (
  priority?: string,
  status?: string
): Promise<Feature[]> => {
  const params: any = {};
  if (priority) params.priority = priority;
  if (status) params.status = status;

  const response = await api.get('/features/with-status', { params });
  return response.data;
};

export const getFeaturesByCategoryWithStatus = async (
  priority?: string,
  status?: string
): Promise<FeaturesByCategory> => {
  const params: any = {};
  if (priority) params.priority = priority;
  if (status) params.status = status;

  const response = await api.get('/features/by-category-with-status', { params });
  return response.data;
};

// ============ IDEAS API ============

export const getIdeas = async (
  category?: string,
  status?: IdeaStatus,
  ideaType?: IdeaType,
  featureId?: string
): Promise<Idea[]> => {
  const params: any = {};
  if (category) params.category = category;
  if (status) params.status = status;
  if (ideaType) params.idea_type = ideaType;
  if (featureId) params.feature_id = featureId;

  const response = await api.get('/ideas', { params });
  return response.data;
};

export const getIdeasByCategory = async (): Promise<IdeasByCategory> => {
  const response = await api.get('/ideas/by-category');
  return response.data;
};

export const getIdea = async (ideaId: number): Promise<Idea> => {
  const response = await api.get(`/ideas/${ideaId}`);
  return response.data;
};

export const createIdea = async (idea: IdeaCreate): Promise<Idea> => {
  const response = await api.post('/ideas', idea);
  return response.data;
};

export const updateIdea = async (ideaId: number, updates: Partial<Idea>): Promise<Idea> => {
  const response = await api.patch(`/ideas/${ideaId}`, updates);
  return response.data;
};

export const deleteIdea = async (ideaId: number): Promise<void> => {
  await api.delete(`/ideas/${ideaId}`);
};

export const acceptIdeaAsFeature = async (
  ideaId: number,
  expectations?: string
): Promise<Idea> => {
  const response = await api.post(`/ideas/${ideaId}/accept-as-feature`, { expectations });
  return response.data;
};

export const acceptIdeaAsTask = async (
  ideaId: number,
  aiTeamMember: string,
  featureId?: string
): Promise<Idea> => {
  const response = await api.post(`/ideas/${ideaId}/accept-as-task`, {
    ai_team_member: aiTeamMember,
    feature_id: featureId
  });
  return response.data;
};

export const rejectIdea = async (ideaId: number): Promise<Idea> => {
  const response = await api.post(`/ideas/${ideaId}/reject`);
  return response.data;
};

export const deferIdea = async (ideaId: number): Promise<Idea> => {
  const response = await api.post(`/ideas/${ideaId}/defer`);
  return response.data;
};

// ============ MODELS API ============

export const getAvailableModels = async (): Promise<string[]> => {
  const response = await api.get('/models/available');
  return response.data.models || [];
};

// ============ QUICKTASK EXECUTION API ============

// Returns a Task object (created as in_progress, updated when complete)
export const executeQuicktask = async (prompt: string, model?: string, person?: string): Promise<Task> => {
  const payload: any = { prompt };
  if (model) {
    payload.model = model;
  }
  if (person) {
    payload.person = person;
  }
  const response = await api.post('/quicktasks/execute', payload);
  return response.data;
};

// ============ JSONL SESSION MONITORING API ============
// Real-time Claude Code session monitoring via JSONL file polling

/**
 * Connect to JSONL WebSocket for rich real-time Claude Code updates.
 * This provides more detailed information than the regular task WebSocket:
 * - Full message text
 * - Tool calls with names and inputs
 * - Tool results
 * - Thinking/reasoning blocks
 * - Todo list updates
 */
export const connectJsonlWebSocket = (
  taskId: number,
  onMessage: (data: JsonlUpdate) => void,
  onConnectionChange?: (connected: boolean) => void
): WebSocket => {
  // Use same host as page (nginx proxies /api/ to backend)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host; // includes port if non-standard
  const ws = new WebSocket(withTokenParam(`${protocol}//${host}/api/ws/jsonl/${taskId}`));

  let pingInterval: NodeJS.Timeout | null = null;
  let hasConnected = false;

  ws.onopen = () => {
    hasConnected = true;
    console.log(`[JSONL WebSocket] Connected for task ${taskId}`);
    if (onConnectionChange) onConnectionChange(true);

    // Ping every 30 seconds to keep connection alive
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      } else {
        if (pingInterval) {
          clearInterval(pingInterval);
          pingInterval = null;
        }
      }
    }, 30000);
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as JsonlUpdate;
      onMessage(data);
    } catch (error) {
      console.error('[JSONL WebSocket] Error parsing message:', error);
    }
  };

  ws.onerror = (error) => {
    if (hasConnected) {
      console.error('[JSONL WebSocket] Error for task', taskId, ':', error);
    }
  };

  ws.onclose = (event) => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }

    if (onConnectionChange) onConnectionChange(false);

    if (hasConnected) {
      if (event.wasClean) {
        console.log(`[JSONL WebSocket] Closed cleanly for task ${taskId}`);
      } else {
        console.warn(`[JSONL WebSocket] Connection closed unexpectedly for task ${taskId}`);
      }
    }
  };

  return ws;
};

/**
 * Get the currently active Claude Code session info.
 */
export const getActiveSession = async (): Promise<ActiveSession> => {
  const response = await api.get('/sessions/active');
  return response.data;
};

/**
 * Get historical messages from a Claude Code session.
 */
export const getSessionHistory = async (
  sessionId?: string,
  limit: number = 100
): Promise<{ messages: JsonlMessage[]; count: number }> => {
  const params: any = { limit };
  if (sessionId) params.session_id = sessionId;
  const response = await api.get('/sessions/history', { params });
  return response.data;
};

/**
 * Manually start JSONL monitoring for a task.
 */
export const startSessionMonitoring = async (taskId: number): Promise<void> => {
  await api.post(`/sessions/monitor/start/${taskId}`);
};

/**
 * Stop JSONL monitoring for a task.
 */
export const stopSessionMonitoring = async (taskId: number): Promise<void> => {
  await api.post(`/sessions/monitor/stop/${taskId}`);
};

// ============ RELEASES API ============

export const listReleases = async (options?: TaskListOptions): Promise<Release[]> => {
  const params: any = {};
  if (options?.search) params.search = options.search;
  if (options?.allTime) params.all_time = true;
  if (options?.days !== undefined) params.days = options.days;

  const response = await api.get('/releases', { params });
  return response.data;
};

export const getRelease = async (releaseId: number): Promise<Release> => {
  const response = await api.get(`/releases/${releaseId}`);
  return response.data;
};

export interface CreateReleaseOptions {
  model?: string;        // release's own steps (notes, re-review, /release-quest)
  fixModel?: string;     // fix tasks spawned from suggestions
  fixMode?: ReleaseFixMode;
  autoLoop?: boolean;    // arm the autonomous loop now; it takes over once notes land
  instructions?: string;
}

export const createRelease = async (options: CreateReleaseOptions = {}): Promise<Release> => {
  const payload: Record<string, unknown> = {};
  if (options.model) payload.model = options.model;
  if (options.fixModel) payload.fix_model = options.fixModel;
  if (options.fixMode) payload.fix_mode = options.fixMode;
  if (options.autoLoop) payload.auto_loop = true;
  if (options.instructions) payload.instructions = options.instructions;
  const response = await api.post('/releases', payload);
  return response.data;
};

export interface ReleaseSettings {
  model?: string | null;
  fix_model?: string | null;
  fix_mode?: ReleaseFixMode | null;
}

export const updateReleaseSettings = async (releaseId: number, settings: ReleaseSettings): Promise<Release> => {
  const response = await api.patch(`/releases/${releaseId}/settings`, settings);
  return response.data;
};

/** Hand every pending suggestion (scope=all) or one area's pending suggestions
 *  (scope=area) to ONE agent in a single task. */
export const startFixBatch = async (
  releaseId: number,
  options: { scope: 'all' | 'area'; area?: string; model?: string; mode?: ReleaseFixMode },
): Promise<Release & { started_task_ids?: number[] }> => {
  const payload: Record<string, unknown> = { scope: options.scope };
  if (options.area) payload.area = options.area;
  if (options.model) payload.model = options.model;
  if (options.mode) payload.mode = options.mode;
  const response = await api.post(`/releases/${releaseId}/fix-batch`, payload);
  return response.data;
};

export const startSuggestionQuicktask = async (
  releaseId: number,
  index: number,
  prompt?: string,
  model?: string
): Promise<Release> => {
  const payload: any = {};
  if (prompt) payload.prompt = prompt;
  if (model) payload.model = model;
  const response = await api.post(
    `/releases/${releaseId}/suggestions/${index}/quicktask`,
    payload
  );
  return response.data;
};

export const updateSuggestion = async (
  releaseId: number,
  index: number,
  text: string
): Promise<Release> => {
  const response = await api.patch(
    `/releases/${releaseId}/suggestions/${index}`,
    { text }
  );
  return response.data;
};

export const addSuggestion = async (
  releaseId: number,
  text: string
): Promise<Release> => {
  const response = await api.post(`/releases/${releaseId}/suggestions`, { text });
  return response.data;
};

export const setSuggestionRejected = async (
  releaseId: number,
  index: number,
  rejected: boolean
): Promise<Release> => {
  const response = await api.patch(
    `/releases/${releaseId}/suggestions/${index}`,
    { rejected }
  );
  return response.data;
};

export const confirmRelease = async (releaseId: number): Promise<Release> => {
  const response = await api.post(`/releases/${releaseId}/confirm`);
  return response.data;
};

export const retryReview = async (releaseId: number): Promise<Release> => {
  const response = await api.post(`/releases/${releaseId}/retry-review`);
  return response.data;
};

export const startAutoLoop = async (releaseId: number, settings?: ReleaseSettings): Promise<Release> => {
  const response = await api.post(`/releases/${releaseId}/auto-loop`, settings || {});
  return response.data;
};

export const stopAutoLoop = async (releaseId: number): Promise<Release> => {
  const response = await api.post(`/releases/${releaseId}/stop-auto-loop`);
  return response.data;
};

export const forceRelease = async (releaseId: number): Promise<Release> => {
  const response = await api.post(`/releases/${releaseId}/force-release`);
  return response.data;
};

export const saveReleaseInstructions = async (releaseId: number, instructions: string): Promise<Release> => {
  const response = await api.patch(`/releases/${releaseId}/instructions`, { instructions });
  return response.data;
};

export const cancelRelease = async (releaseId: number): Promise<Release> => {
  const response = await api.post(`/releases/${releaseId}/cancel`);
  return response.data;
};

export const deleteRelease = async (releaseId: number): Promise<void> => {
  await api.delete(`/releases/${releaseId}`);
};

// ============ CONTENT / INITIATIVES API ============

// Domains an initiative can belong to (used for role-based filtering).
export type InitiativeDomain = 'product' | 'growth' | 'impact' | 'content' | 'mission';

export interface InitiativeSummary {
  id: string;
  name?: string;
  mission_goal?: string;
  status?: string;
  owner?: string;
  platforms?: string[];
  quest_id?: string;
  domain?: InitiativeDomain;
  [key: string]: any;
}

// Raw file strings backing an initiative's context (mirrors how Features expose
// their editable folder context). Any subset can be saved back via PUT.
export interface InitiativeContext {
  initiative_yaml: string;
  context_md: string;
  standards_md: string;
  references_yaml: string;
}

// A general (non-content) task created against an initiative.
export interface InitiativeTask {
  id?: string | number;
  title?: string;
  description?: string;
  status?: string;
  ai_team_member?: string;
  model?: string;
  [key: string]: any;
}

export interface Initiative extends InitiativeSummary {
  standards?: string;
  context?: string;
  references?: any[];
}

export interface PieceSummary {
  id: string;
  initiative_id?: string;
  platform?: string;
  type?: string;
  status?: string;
  iterations?: number;
  brief?: string;
  created_at?: string;
  updated_at?: string;
  reject_reason?: string;
  [key: string]: any;
}

export interface PieceChangeRequest {
  at?: string;
  notes?: string;
}

export interface PieceQuestion {
  dependency_id?: number | string;
  question?: string;
  owner_role?: string;
  asked_at?: string;
}

// The generated image reference stored on an image piece.
export interface PieceImage {
  prompt?: string;
  model?: string;
  url?: string;
  b64_file?: string;
  generated_at?: string;
}

export interface Piece extends PieceSummary {
  // draft.md (the current draft) and review.md (the rendered
  // generate->critic iteration trail) are returned as raw strings.
  draft?: string;
  review?: string;
  image_prompt?: string;
  image?: PieceImage;
  change_requests?: PieceChangeRequest[];
  questions?: PieceQuestion[];
  // Async generation: the backing cockpit task to watch live, the time the run
  // started, and any error message when status === 'error'.
  task_id?: number | null;
  gen_started_at?: string | null;
  gen_error?: string | null;
}

// A supported content platform from content/platforms.yaml (the FULL list,
// used to populate the create-piece picker regardless of the initiative's
// declared subset).
export interface ContentPlatform {
  id: string;
  name?: string;
  char_limit?: number;
  format?: string;
  tone?: string;
  image_required?: boolean;
  [key: string]: any;
}

// A question raised on a piece, with its answer (if the human has answered).
export interface PieceQuestionAnswer {
  question: string;
  owner_role?: string;
  asked_at?: string;
  answer?: string | null;
}

export const listInitiatives = async (): Promise<InitiativeSummary[]> => {
  const response = await api.get('/content/initiatives');
  return response.data;
};

export const getInitiative = async (initiativeId: string): Promise<Initiative> => {
  const response = await api.get(`/content/initiatives/${initiativeId}`);
  return response.data;
};

export const listPieces = async (initiativeId: string): Promise<PieceSummary[]> => {
  const response = await api.get(`/content/initiatives/${initiativeId}/pieces`);
  return response.data;
};

export const getPiece = async (initiativeId: string, pieceId: string): Promise<Piece> => {
  const response = await api.get(`/content/initiatives/${initiativeId}/pieces/${pieceId}`);
  return response.data;
};

export const createPiece = async (
  initiativeId: string,
  platform: string,
  pieceType: string = 'post',
  brief: string = '',
  imagePrompt: string = ''
): Promise<Piece> => {
  const response = await api.post(`/content/initiatives/${initiativeId}/pieces`, {
    platform,
    piece_type: pieceType,
    brief,
    image_prompt: imagePrompt,
  });
  return response.data;
};

export const listPieceTypes = async (): Promise<{ types: string[]; text_types: string[]; image_types: string[] }> => {
  const response = await api.get('/content/piece-types');
  return response.data;
};

// The FULL list of supported platforms (not just an initiative's subset). The
// create-piece picker uses this, plus an "Other (specify)" free-text option.
export const listContentPlatforms = async (): Promise<ContentPlatform[]> => {
  const response = await api.get('/content/platforms');
  return response.data?.platforms || [];
};

export const generatePiece = async (initiativeId: string, pieceId: string): Promise<any> => {
  const response = await api.post(`/content/initiatives/${initiativeId}/pieces/${pieceId}/generate`);
  return response.data;
};

export const approvePiece = async (initiativeId: string, pieceId: string): Promise<Piece> => {
  const response = await api.post(`/content/initiatives/${initiativeId}/pieces/${pieceId}/approve`);
  return response.data;
};

export const rejectPiece = async (
  initiativeId: string,
  pieceId: string,
  reason: string = ''
): Promise<Piece> => {
  const response = await api.post(`/content/initiatives/${initiativeId}/pieces/${pieceId}/reject`, {
    reason,
  });
  return response.data;
};

// Human requests changes on a piece (-> revision_requested). The next Generate
// folds these notes into the AI's prompt so it sees what to improve.
export const requestPieceChanges = async (
  initiativeId: string,
  pieceId: string,
  notes: string
): Promise<Piece> => {
  const response = await api.post(
    `/content/initiatives/${initiativeId}/pieces/${pieceId}/request-changes`,
    { notes }
  );
  return response.data;
};

// AI asks the human a question (creates a 'question' dependency for a role).
export const askPieceQuestion = async (
  initiativeId: string,
  pieceId: string,
  question: string,
  ownerRole: string
): Promise<Piece> => {
  const response = await api.post(
    `/content/initiatives/${initiativeId}/pieces/${pieceId}/ask-question`,
    { question, owner_role: ownerRole }
  );
  return response.data;
};

// The piece's questions + any answers (answers come from the Dependencies inbox).
export const listPieceQuestions = async (
  initiativeId: string,
  pieceId: string
): Promise<PieceQuestionAnswer[]> => {
  const response = await api.get(
    `/content/initiatives/${initiativeId}/pieces/${pieceId}/questions`
  );
  return response.data.questions || [];
};

// ---- Initiative context (editable raw files) -------------------------------

export const getInitiativeContext = async (initiativeId: string): Promise<InitiativeContext> => {
  const response = await api.get(`/content/initiatives/${initiativeId}/context`);
  return response.data;
};

// Save any subset of the 4 context files. The body sent is exactly `fields`.
export const saveInitiativeContext = async (
  initiativeId: string,
  fields: Partial<InitiativeContext>
): Promise<InitiativeContext> => {
  const response = await api.put(`/content/initiatives/${initiativeId}/context`, fields);
  return response.data;
};

// ---- Content Guidelines (the initiative's voice/editorial standards) -------
// Backed by standards.md; applied to EVERY piece generated for the initiative.

export const getContentGuidelines = async (initiativeId: string): Promise<string> => {
  const response = await api.get(`/content/initiatives/${initiativeId}/guidelines`);
  return response.data.guidelines ?? '';
};

export const saveContentGuidelines = async (
  initiativeId: string,
  guidelines: string
): Promise<string> => {
  const response = await api.put(`/content/initiatives/${initiativeId}/guidelines`, { guidelines });
  return response.data.guidelines ?? '';
};

// ---- Initiative general (non-content) tasks --------------------------------

export const getInitiativeTasks = async (initiativeId: string): Promise<InitiativeTask[]> => {
  const response = await api.get(`/content/initiatives/${initiativeId}/tasks`);
  return response.data;
};

export const createInitiativeTask = async (
  initiativeId: string,
  payload: { title: string; description: string; ai_team_member: string; model?: string }
): Promise<InitiativeTask> => {
  const response = await api.post(`/content/initiatives/${initiativeId}/tasks`, payload);
  return response.data;
};

// ============ PERSON LANE + INTAKE API (AI Productivity OS) ============

export type LaneStatus = 'queued' | 'in_progress' | 'ready_for_review' | 'completed' | 'failed';

// A linkable cockpit task in a person's lane. Carries the id so the frontend can
// deep-link to the task's detail view (where a human reads the AI output and
// responds/completes).
export interface LaneTask {
  id: number | null;
  title: string | null;
  status: LaneStatus;
  // Who created the task, and whether it was an AI rep / automated process (vs. a human
  // acting in the UI). Lets the lane badge tasks a rep spawned on its own.
  created_by?: string | null;
  created_by_ai?: boolean;
  // Who executes the task. A ticket you created can sit in your lane while being
  // assigned to another rep; this lets the lane label those rows.
  ai_team_member?: string | null;
}

export interface PersonLane {
  person: string;
  tasks: {
    counts: Record<LaneStatus, number>;
    by_status: Record<LaneStatus, LaneTask[]>;
    total: number;
  };
  // Open team decision-requests directed at this person ("My Teams" asks).
  // Shape is Quest-defined; kept loose since it degrades to [] when Quest isn't configured.
  decisions: any[];
  asks_count: number;
  decisions_note?: string;
  decisions_error?: string;
}

export const getPersonLane = async (person: string): Promise<PersonLane> => {
  const response = await api.get(`/person/${person}/lane`);
  return response.data;
};

// ---- Manage your AI representative: view/edit its SKILL.md + memory.md ------

export interface PersonSkill {
  skill_md: string;
  memory_md: string;
}

export const getPersonSkill = async (person: string): Promise<PersonSkill> => {
  const response = await api.get(`/person/${person}/skill`);
  return response.data;
};

export const savePersonSkill = async (
  person: string,
  payload: { skill_md?: string; memory_md?: string }
): Promise<PersonSkill> => {
  const response = await api.put(`/person/${person}/skill`, payload);
  return response.data;
};

export interface IntakePlan {
  action: string; // "execute" | "decision" | ...
  agent?: string;
  task?: any;
  created_task_id?: number | null;
  [key: string]: any;
}

export const submitIntake = async (
  text: string,
  source: string = 'manual',
  author?: string,
  model?: string
): Promise<IntakePlan> => {
  const payload: any = { text, source };
  if (author) payload.author = author;
  if (model) payload.model = model;
  const response = await api.post('/intake', payload);
  return response.data;
};

// ============ CONVERSATIONAL CHAT with a person's AI agent ============
// A real back-and-forth chat (ChatGPT-style). Distinct from submitIntake, which
// always creates a task. Chat replies conversationally and only spins off a task
// when the message is an explicit request to DO work (created_task_id is then set).

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface ChatReply {
  reply: string;
  created_task_id?: number | null;
}

export const sendChat = async (
  person: string,
  message: string,
  model?: string
): Promise<ChatReply> => {
  const payload: any = { message, author: person };
  if (model) payload.model = model;
  const response = await api.post(`/person/${person}/chat`, payload);
  return response.data;
};

export const getChatHistory = async (person: string): Promise<ChatMessage[]> => {
  const response = await api.get(`/person/${person}/chat`);
  return response.data.messages;
};

// ============ AUTOMATION API (attribution + outbound email) ============

// Attribution summary. When the integration isn't configured the backend
// returns a graceful { configured: false, note } shape rather than erroring.
export interface AttributionSummary {
  configured?: boolean;
  note?: string;
  [key: string]: any;
}

export const getAttributionSummary = async (): Promise<AttributionSummary> => {
  const response = await api.get('/automation/attribution/summary');
  return response.data;
};

export interface EmailSendResult {
  // ok/sent on success; configured:false when no Resend key is present.
  ok?: boolean;
  sent?: boolean;
  configured?: boolean;
  note?: string;
  id?: string;
  [key: string]: any;
}

export const sendEmail = async (
  to: string,
  subject: string,
  html: string,
  fromAddr?: string
): Promise<EmailSendResult> => {
  const payload: any = { to, subject, html };
  if (fromAddr) payload.from_addr = fromAddr;
  const response = await api.post('/automation/email', payload);
  return response.data;
};

// ---- Integrations (dependency surface: API keys & tokens) -----------------
// The status surface returns ONLY booleans — never secret values. The setter
// returns {ok, configured} and never echoes the value back.
export interface IntegrationStatus {
  key: string;
  label: string;
  owner_role: string;
  purpose: string;
  configured: boolean;
}

export const getIntegrationsStatus = async (): Promise<IntegrationStatus[]> => {
  const response = await api.get('/integrations/status');
  return response.data.integrations || [];
};

export const setIntegration = async (
  key: string,
  value: string
): Promise<{ ok: boolean; configured: boolean }> => {
  const response = await api.post('/integrations/set', { key, value });
  return response.data;
};

// ---- Dependency Requests (generic blocked-on-a-person surface) ------------
// A dependency = "something is blocked on a specific person/role; here's how to
// resolve it." api_key is just ONE kind (auto-derived from the integrations
// catalog). SECURITY: the API NEVER returns secret values — for api_key deps the
// payload carries only the env var NAME, and resolution returns a configured flag.
export type DependencyKind =
  | 'api_key'
  | 'approval'
  | 'info'
  | 'access'
  | 'decision'
  | 'task'
  | 'question'
  | 'other';

export interface Dependency {
  id: number | string;        // synthetic api_key deps use a string id ("apikey:ENV")
  synthetic: boolean;
  title: string;
  description?: string | null;
  kind: DependencyKind;
  owner_role: string;
  owner_person?: string | null;
  status: 'open' | 'resolved';
  source?: string | null;
  payload?: any;              // kind-specific metadata (never a secret)
  resolution?: string | null;
  created_at?: string | null;
  resolved_at?: string | null;
}

export const getDependencies = async (
  ownerRole?: string,
  status: string = 'open'
): Promise<Dependency[]> => {
  const params: Record<string, string> = { status };
  if (ownerRole) params.owner_role = ownerRole;
  const response = await api.get('/dependencies', { params });
  return response.data.dependencies || [];
};

export const createDependency = async (input: {
  title: string;
  description?: string;
  kind: DependencyKind;
  owner_role: string;
  owner_person?: string;
  source?: string;
  payload?: any;
}): Promise<Dependency> => {
  const response = await api.post('/dependencies', input);
  return response.data;
};

// Resolve a dependency. For api_key kind, value is the secret key (write-only,
// never returned). For other kinds, value is a resolution note.
export const resolveDependency = async (
  id: number | string,
  value?: string
): Promise<any> => {
  const response = await api.post(`/dependencies/${id}/resolve`, { value });
  return response.data;
};

// Dismiss a directed ask/dependency: clear it out of the lane without answering.
export const dismissDependency = async (id: number | string): Promise<any> => {
  const response = await api.post(`/dependencies/${id}/dismiss`);
  return response.data;
};

// Hard-delete a stored dependency row entirely.
export const deleteDependency = async (id: number | string): Promise<any> => {
  const response = await api.delete(`/dependencies/${id}`);
  return response.data;
};

// "Let my AI handle it" for a directed ask/dependency.
export const handleDependencyByAi = async (id: number | string): Promise<HandleByAiResult> => {
  const response = await api.post(`/dependencies/${id}/handle-by-ai`);
  return response.data;
};

// ============================================================================
// Automated team reporting
// Agents draft weekly/monthly check-in reports from each person's task data;
// nothing is submitted to the official check-ins until a human approves it.
// ============================================================================

export type ReportPeriodType = 'weekly' | 'monthly';
export type ReportDraftStatus =
  | 'generating'
  | 'pending_review'
  | 'approved'
  | 'submitted'
  | 'failed'
  | 'dismissed';

export const REPORT_TEAMS = [
  'AI Platform',
  'Application Engineering',
  'DevOps',
  'Growth',
  'Impact',
  'People',
  'Product',
  'Science',
  'Executive',
] as const;

export type ReportTeam = (typeof REPORT_TEAMS)[number];

export interface ReportRequestItem {
  team: string;
  text: string;
}

export interface ReportDeliverableItem {
  text: string;
  team?: string;
}

export interface ReportDraft {
  id: number;
  person: string;
  period_type: ReportPeriodType;
  year: number;
  period: number;
  label: string;
  status: ReportDraftStatus;
  accomplished: string;
  planned: string;
  challenges: string;
  requests: ReportRequestItem[];
  deliverables: ReportDeliverableItem[];
  carry_over_deliverables: string[];
  deliverables_completed: number | null;
  deliverables_total: number | null;
  checkins_report_id: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  submitted_at: string | null;
  error: string | null;
  detail_email_sent_at: string | null;
  detail_email_error: string | null;
}

export interface ReportBatchResult {
  period_type: ReportPeriodType;
  year: number;
  period: number;
  label: string;
  count: number;
  generating: number;
  skipped: number;
  drafted: string[];
  // Current field name: people who have never actually used the Command Center (login or
  // chat), so no draft was made for them. `skipped_never_logged_in` is kept as a fallback
  // for a brief window: the backend still emits both, but only the new key is guaranteed
  // once it redeploys.
  skipped_never_used_cockpit?: string[];
  skipped_never_logged_in?: string[];
  drafts: ReportDraft[];
}

export const getReportsConfig = async (): Promise<{ configured: boolean; reason: string | null }> => {
  const response = await api.get('/reports/config');
  return response.data;
};

export const generateReportBatch = async (params: {
  period_type: ReportPeriodType;
  year?: number;
  period?: number;
  people?: string[];
  created_by?: string;
  include_never_used_cockpit?: boolean;
}): Promise<ReportBatchResult> => {
  const response = await api.post('/reports/batch', params);
  return response.data;
};

export const listReportDrafts = async (params: {
  period_type?: ReportPeriodType;
  year?: number;
  period?: number;
  status?: ReportDraftStatus;
  person?: string;
}): Promise<{ count: number; drafts: ReportDraft[] }> => {
  const response = await api.get('/reports/drafts', { params });
  return response.data;
};

export const getReportDraft = async (id: number): Promise<ReportDraft> => {
  const response = await api.get(`/reports/drafts/${id}`);
  return response.data;
};

export const patchReportDraft = async (
  id: number,
  patch: Partial<
    Pick<
      ReportDraft,
      | 'accomplished'
      | 'planned'
      | 'challenges'
      | 'requests'
      | 'deliverables'
      | 'carry_over_deliverables'
      | 'deliverables_completed'
      | 'deliverables_total'
    >
  >
): Promise<ReportDraft> => {
  const response = await api.patch(`/reports/drafts/${id}`, patch);
  return response.data;
};

export const approveReportDraft = async (
  id: number
): Promise<{ ok: boolean; submitted?: boolean; reason?: string; draft?: ReportDraft }> => {
  const response = await api.post(`/reports/drafts/${id}/approve`);
  return response.data;
};

export const dismissReportDraft = async (id: number): Promise<ReportDraft> => {
  const response = await api.post(`/reports/drafts/${id}/dismiss`);
  return response.data;
};

export const approveAllReportDrafts = async (params: {
  period_type: ReportPeriodType;
  year: number;
  period: number;
}): Promise<{ ok: boolean; submitted?: number; total?: number; reason?: string }> => {
  const response = await api.post('/reports/drafts/approve-all', params);
  return response.data;
};

// ============================================================================
// Email Approval Queue
// AI files outbound email drafts (weekly reports, proposals, team comms, donor
// outreach); nothing is sent until a human reviews and approves a draft here.
// Approving a draft sends it via Resend.
// ============================================================================
export type EmailDraftStatus =
  | 'pending_review'
  | 'approved'
  | 'sent'
  | 'failed'
  | 'rejected';

export interface EmailDraft {
  id: number;
  person: string;
  to_addrs: string;
  cc_addrs: string | null;
  bcc_addrs: string | null;
  reply_to: string | null;
  from_addr: string | null;
  subject: string;
  body: string;
  context: string | null;
  source_task_id: number | null;
  status: EmailDraftStatus;
  created_by: string | null;
  approved_by: string | null;
  resend_id: string | null;
  error: string | null;
  rejected_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  sent_at: string | null;
}

export const getEmailConfig = async (): Promise<{
  configured: boolean;
  reason: string | null;
  default_from: string | null;
}> => {
  const response = await api.get('/email-drafts/config');
  return response.data;
};

export const listEmailDrafts = async (params: {
  status?: EmailDraftStatus;
  person?: string;
  source_task_id?: number;
} = {}): Promise<{ count: number; drafts: EmailDraft[] }> => {
  const response = await api.get('/email-drafts', { params });
  return response.data;
};

export const getEmailDraft = async (id: number): Promise<EmailDraft> => {
  const response = await api.get(`/email-drafts/${id}`);
  return response.data;
};

export const patchEmailDraft = async (
  id: number,
  patch: Partial<
    Pick<
      EmailDraft,
      'to_addrs' | 'cc_addrs' | 'bcc_addrs' | 'reply_to' | 'from_addr' | 'subject' | 'body' | 'context'
    >
  >
): Promise<EmailDraft> => {
  const response = await api.patch(`/email-drafts/${id}`, patch);
  return response.data;
};

export const approveEmailDraft = async (
  id: number,
  approvedBy?: string
): Promise<{
  ok: boolean;
  sent: boolean;
  reason?: string;
  draft?: EmailDraft;
  task_completed?: number | null;
}> => {
  const response = await api.post(`/email-drafts/${id}/approve`, { approved_by: approvedBy });
  return response.data;
};

export const deleteEmailDraft = async (id: number): Promise<{ ok: boolean; deleted: number }> => {
  const response = await api.delete(`/email-drafts/${id}`);
  return response.data;
};

export const rejectEmailDraft = async (
  id: number,
  reason?: string
): Promise<EmailDraft> => {
  const response = await api.post(`/email-drafts/${id}/reject`, { reason });
  return response.data;
};

export const createEmailDraft = async (draft: {
  person: string;
  to_addrs: string;
  subject: string;
  body: string;
  cc_addrs?: string;
  context?: string;
  source_task_id?: number;
  created_by?: string;
}): Promise<EmailDraft> => {
  const response = await api.post('/email-drafts', draft);
  return response.data;
};

export const resolveQuestDecision = async (
  decisionId: string,
  resolution: string
): Promise<{ status: string; decision_id: string; result: any }> => {
  const response = await api.post(`/decisions/${decisionId}/resolve`, {
    decision_id: decisionId,
    resolution: resolution,
  });
  return response.data;
};

// ============================================================================
// Email Campaigns — bulk personalized sends (a name, subject, and template
// body drip out to a staged recipient list). Staged as a draft, a human
// approves before anything sends, then the backend drips it out over time.
// ============================================================================
export type EmailCampaignStatus =
  | 'draft'
  | 'awaiting_joshua_preview'
  | 'approved'
  | 'sending'
  | 'paused'
  | 'sent'
  | 'failed'
  | 'cancelled';

export type CampaignRecipientStatus = 'pending' | 'sent' | 'failed' | 'skipped';

export interface CampaignProgress {
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  total: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
}

export interface EmailCampaign {
  id: number;
  name: string;
  person: string;
  from_addr: string | null;
  reply_to: string | null;
  subject: string;
  template_body: string;
  status: EmailCampaignStatus;
  interval_seconds: number;
  source_csv: string | null;
  created_by: string | null;
  approved_by: string | null;
  error: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  archived_at: string | null;
  // Why Delete is refused for this campaign, or null when deleting it is safe. Sent by the
  // backend so the button can explain itself before it is clicked.
  delete_blocked_reason: string | null;
  progress: CampaignProgress;
}

export interface CampaignRecipient {
  id: number;
  campaign_id: number;
  email: string;
  first_name: string | null;
  full_name: string | null;
  body: string;
  status: CampaignRecipientStatus;
  resend_id: string | null;
  error: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  unsubscribed_at: string | null;
}

export const getCampaignConfig = async (): Promise<{
  configured: boolean;
  reason: string | null;
}> => {
  const response = await api.get('/email-campaigns/config');
  return response.data;
};

export const listCampaigns = async (params: {
  status?: EmailCampaignStatus;
  person?: string;
  include_archived?: boolean;
} = {}): Promise<{ count: number; campaigns: EmailCampaign[] }> => {
  const response = await api.get('/email-campaigns', { params });
  return response.data;
};

export const getCampaign = async (id: number): Promise<EmailCampaign> => {
  const response = await api.get(`/email-campaigns/${id}`);
  return response.data;
};

export const listCampaignRecipients = async (
  id: number,
  params: { status?: CampaignRecipientStatus; limit?: number; offset?: number } = {}
): Promise<{ count: number; recipients: CampaignRecipient[] }> => {
  const response = await api.get(`/email-campaigns/${id}/recipients`, { params });
  return response.data;
};

export const createCampaign = async (campaign: {
  name: string;
  person?: string;
  subject: string;
  template_body: string;
  source_csv?: string;
  limit?: number;
  recipients?: { email: string; first_name?: string; full_name?: string }[];
  from_addr?: string;
  reply_to?: string;
  interval_seconds?: number;
  sender_signature?: string;
  created_by?: string;
}): Promise<EmailCampaign> => {
  const response = await api.post('/email-campaigns', campaign);
  return response.data;
};

export const recordPreviewGo = async (id: number, note: string): Promise<EmailCampaign> => {
  const response = await api.post(`/email-campaigns/${id}/record-preview-go`, { note });
  return response.data;
};

export const approveCampaign = async (id: number, approvedBy: string): Promise<EmailCampaign> => {
  const response = await api.post(`/email-campaigns/${id}/approve`, { approved_by: approvedBy });
  return response.data;
};

export const sendCampaign = async (id: number): Promise<EmailCampaign> => {
  const response = await api.post(`/email-campaigns/${id}/send`);
  return response.data;
};

export const pauseCampaign = async (id: number): Promise<EmailCampaign> => {
  const response = await api.post(`/email-campaigns/${id}/pause`);
  return response.data;
};

export const cancelCampaign = async (id: number): Promise<EmailCampaign> => {
  const response = await api.post(`/email-campaigns/${id}/cancel`);
  return response.data;
};

// Hard-delete, only ever allowed for a campaign that never sent an email (the backend refuses
// anything else with 409 and the reason carried in delete_blocked_reason).
export const deleteCampaign = async (id: number): Promise<{ deleted: boolean; id: number }> => {
  const response = await api.delete(`/email-campaigns/${id}`);
  return response.data;
};

// Hide a campaign off the default list without destroying it. This is how a campaign that
// actually sent leaves the list, since its opens/clicks are the only record of the send.
export const archiveCampaign = async (id: number): Promise<EmailCampaign> => {
  const response = await api.post(`/email-campaigns/${id}/archive`);
  return response.data;
};

export const unarchiveCampaign = async (id: number): Promise<EmailCampaign> => {
  const response = await api.post(`/email-campaigns/${id}/unarchive`);
  return response.data;
};

// Cross-campaign engagement per contact (open/click rate across every campaign an address was
// actually sent), sorted so the most consistent openers rise to the top.
export interface ContactEngagement {
  email: string;
  first_name: string | null;
  full_name: string | null;
  campaigns_sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
  open_rate: number;
  click_rate: number;
  last_sent_at: string | null;
  last_opened_at: string | null;
  last_clicked_at: string | null;
}

export const getEngagementReport = async (params: {
  min_campaigns?: number;
  limit?: number;
} = {}): Promise<{
  count: number;
  contacts: ContactEngagement[];
  totals: { unique_contacts: number; campaigns_counted: number };
}> => {
  const response = await api.get('/email-campaigns/engagement-report', { params });
  return response.data;
};

// ============================================================================
// Gmail Inbox — the "sign that a response arrived" surface. The cockpit reads
// two mailboxes (support@spiritualdata.org, where campaign/outreach replies
// land via reply-to, and godswill@spiritualdata.org, Zee's own inbox), threads
// each message back to the campaign/recipient/draft it answers where possible,
// and tracks unseen/seen state. See backend/app/services/gmail_inbox_service.py.
// ============================================================================

export type InboxMatchMethod =
  | 'resend_id'
  | 'joshua_preview'
  | 'joshua_preview_ambiguous'
  | 'recipient_address'
  | 'draft_address'
  | 'unmatched';

export interface InboxMessage {
  id: number;
  provider_message_id: string;
  mailbox: string | null;
  from_addr: string;
  from_name: string | null;
  subject: string | null;
  snippet: string | null;
  // Plain text only — render as text (white-space: pre-wrap), never as HTML.
  body_text: string | null;
  received_at: string | null;
  campaign_id: number | null;
  recipient_id: number | null;
  email_draft_id: number | null;
  // Set only when email_draft_id is set and the backend could resolve the sent draft: use these
  // to label a reply to a single Approval Queue email instead of showing it as "unmatched".
  email_draft_subject: string | null;
  email_draft_to_addrs: string | null;
  email_draft_person: string | null;
  match_method: InboxMatchMethod | null;
  is_joshua_preview_reply: boolean;
  preview_go_detected: boolean;
  seen_at: string | null;
  // Set once an AI reply has been drafted for this message (the email_drafts.id it filed into,
  // pending_review in the Approval Queue). Null means no draft has been requested yet.
  reply_draft_id: number | null;
  created_at: string | null;
}

export interface InboxSummary {
  total_unseen: number;
  total_messages: number;
  by_mailbox: Record<string, { unseen: number; total: number }>;
  by_campaign: Record<string, { unseen: number; total: number; campaign_name: string | null }>;
  unmatched_unseen: number;
  // Unseen replies to a single Approval Queue email (email_draft_id set) — distinct from both
  // campaign replies and truly unmatched general mail.
  draft_reply_unseen: number;
}

export interface InboxSyncMailboxResult {
  configured: boolean;
  fetched: number;
  new: number;
  matched: number;
  preview_go_detected: number;
  errors: number;
  reason?: string;
  error?: string;
}

export interface InboxSyncResult {
  configured: boolean;
  by_mailbox: Record<string, InboxSyncMailboxResult>;
  fetched: number;
  new: number;
  matched: number;
  preview_go_detected: number;
  errors: number;
}

export const getInboxConfig = async (): Promise<{ configured: boolean; reason: string | null }> => {
  const response = await api.get('/inbox/config');
  return response.data;
};

export const syncInbox = async (): Promise<InboxSyncResult> => {
  const response = await api.post('/inbox/sync');
  return response.data;
};

export const listInboxMessages = async (params: {
  mailbox?: string;
  campaign_id?: number;
  unmatched_only?: boolean;
  draft_reply_only?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<{ count: number; messages: InboxMessage[] }> => {
  const response = await api.get('/inbox/messages', { params });
  return response.data;
};

export const getInboxSummary = async (): Promise<InboxSummary> => {
  const response = await api.get('/inbox/summary');
  return response.data;
};

export const markInboxMessageSeen = async (id: number): Promise<{ ok: boolean }> => {
  const response = await api.post(`/inbox/messages/${id}/seen`);
  return response.data;
};

export const markAllInboxSeen = async (params: {
  mailbox?: string;
  campaign_id?: number;
} = {}): Promise<{ marked: number }> => {
  const response = await api.post('/inbox/mark-all-seen', null, { params });
  return response.data;
};

export interface DraftInboxReplyResult {
  draft?: { id: number; person: string; to_addrs: string; subject: string; status: string; [key: string]: any };
  created?: boolean;
  error?: string;
}

// AI-drafts a reply to one inbox message, filed into the Approval Queue as pending_review --
// NEVER auto-sent. Idempotent: calling this again for the same message returns the existing
// draft rather than generating a second one.
export const draftInboxReply = async (messageId: number): Promise<DraftInboxReplyResult> => {
  const response = await api.post(`/inbox/messages/${messageId}/draft-reply`);
  return response.data;
};

// ============================================================================
// Quest Asks: Joshua's feedback on team work arrives as a reply to a Quest
// email, which Quest tracks as an "ask" (the ledger behind "What You Asked" in
// the Quest app). Zee works in this cockpit, not the Quest app, so this surface
// brings those asks here as a first-class source alongside the mailboxes. See
// backend/app/services/quest_ask_bridge.py.
// ============================================================================

// kind and state are read as plain strings (not a closed union) since Quest's own vocabulary can
// grow server-side; the known values are documented on the fields below for building filters/menus.
export interface QuestAsk {
  key: string;
  quest_id: string;
  quest_title: string;
  quest_link: string;
  source: string;
  text: string;
  author: string;
  cc: string[];
  occurred_at: string;
  kind: string; // request | standing | context | question | unknown
  state: string; // open | in progress | partially applied | needs reapplying | done | in force | declined | noted
  still_owed: boolean;
  last_note: string;
  seen_at: string | null;
}

export type QuestAskByQuest = Record<string, { title: string; total: number; unseen: number }>;

export interface QuestAskListResponse {
  items: QuestAsk[];
  total: number;
  unseen: number;
  by_quest: QuestAskByQuest;
}

export const listQuestAsks = async (params: {
  state?: string;
  quest_id?: string;
  unseen_only?: boolean;
  still_owed_only?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<QuestAskListResponse> => {
  const response = await api.get('/quest-asks', { params });
  return response.data;
};

export const syncQuestAsks = async (): Promise<{ synced: number; new: number; updated: number }> => {
  const response = await api.post('/quest-asks/sync');
  return response.data;
};

export const markQuestAskSeen = async (key: string): Promise<{ ok: boolean }> => {
  const response = await api.post(`/quest-asks/${encodeURIComponent(key)}/seen`);
  return response.data;
};

export const markAllQuestAsksSeen = async (): Promise<{ marked: number }> => {
  const response = await api.post('/quest-asks/mark-all-seen');
  return response.data;
};

// Sets an ask's state (at minimum, marking it done) with an optional note -- this is the action
// that turns the ask list from a feed into a worklist. Returns the updated QuestAsk.
export const updateQuestAskState = async (
  key: string,
  patch: { state: string; note?: string }
): Promise<QuestAsk> => {
  const response = await api.patch(`/quest-asks/${encodeURIComponent(key)}`, patch);
  return response.data;
};
